import type { CSSProperties, ReactNode } from 'react'
import { useDelayedLoading } from '../../lib/use-delayed-loading'

interface SkeletonProps {
  width?: number | string
  height?: number | string
  radius?: number
  style?: CSSProperties
  className?: string
  /**
   * When provided, enables gated rendering:
   * - `loading={true}`: show skeleton bars after `delayMs` (default 150 ms).
   * - `loading={false}`: render `children` (or null when no children given).
   *
   * Omitting `loading` (legacy usage) renders the skeleton bars immediately,
   * preserving full backward compatibility with existing call sites.
   */
  loading?: boolean
  /**
   * Milliseconds to wait before showing the skeleton when `loading` is true.
   * Prevents a flash of skeleton on cached / sub-150 ms responses.
   * Only respected when `loading` is explicitly passed.
   * @default 150
   */
  delayMs?: number
  /** Content to render when `loading` is false. */
  children?: ReactNode
}

const ANIMATION_NAME = 'lms-skeleton-pulse'

/**
 * Render a placeholder block with a subtle pulsing background. The
 * keyframes are inlined via a `<style>` block (one per <Skeleton> mount
 * is fine — browsers dedupe identical rules; React keeps it idempotent
 * inside the same tree). Reduced-motion users get a static block via
 * the global media-query rule in `tokens.css`.
 *
 * ### Gated rendering (new)
 * Pass `loading` + optionally `children` to opt in to delay-debounced
 * skeleton rendering. Skeleton bars only appear after `delayMs` (150 ms
 * default), so cached responses never cause a flash.
 *
 * ```tsx
 * <Skeleton loading={query.isPending}>
 *   <ActualContent data={query.data} />
 * </Skeleton>
 * ```
 *
 * ### Static placeholder (legacy)
 * Omit `loading` — renders the skeleton bar immediately. Existing call sites
 * continue to work without any changes.
 */
export function Skeleton({
  width = '100%',
  height = 16,
  radius = 6,
  style,
  className,
  loading,
  delayMs = 150,
  children,
}: SkeletonProps): JSX.Element | null {
  // When `loading` prop is not provided (legacy mode), pass a stable false
  // so the hook is always called unconditionally. The bars are shown below
  // regardless of the hook result in ungated mode.
  const gated = loading !== undefined
  const showSkeleton = useDelayedLoading(gated ? loading : false, delayMs)

  // Gated mode: delegate to delayed hook result.
  if (gated) {
    if (!showSkeleton) {
      // Either still within delay window or loading is false — show children.
      return children != null ? <>{children}</> : null
    }
    // Delay elapsed with loading still true — fall through to render bars.
  }
  // Legacy (ungated) mode: always render bars.
  const baseStyle: CSSProperties = {
    display: 'block',
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
    borderRadius: radius,
    background: 'var(--surface-2)',
    animation: `${ANIMATION_NAME} 1.4s var(--motion-ease-inout) infinite`,
  }

  return (
    <>
      <style>{`
        @keyframes ${ANIMATION_NAME} {
          0%, 100% { background-color: var(--surface-2); }
          50% { background-color: var(--surface-2); }
        }
      `}</style>
      <span
        data-testid="skeleton"
        aria-hidden="true"
        className={className}
        style={{ ...baseStyle, ...style }}
      />
    </>
  )
}

interface SkeletonRowProps {
  height?: number
  style?: CSSProperties
}

/** Full-width 16-px row, rounded corners. Matches a single line of body text. */
export function SkeletonRow({ height = 16, style }: SkeletonRowProps): JSX.Element {
  if (style) {
    return <Skeleton width="100%" height={height} radius={6} style={style} />
  }
  return <Skeleton width="100%" height={height} radius={6} />
}

interface SkeletonTextProps {
  lines?: number
  /** Optional gap between lines, in px. */
  gap?: number
}

/**
 * Multi-line text placeholder. Default 3 lines; the last line is 60%
 * width to mimic natural paragraph rag.
 */
export function SkeletonText({ lines = 3, gap = 8 }: SkeletonTextProps): JSX.Element {
  const rows = Array.from({ length: lines }, (_, i) => i)
  return (
    <div
      data-testid="skeleton-text"
      style={{ display: 'flex', flexDirection: 'column', gap }}
    >
      {rows.map((i) => (
        <Skeleton
          key={i}
          width={i === rows.length - 1 ? '60%' : '100%'}
          height={12}
          radius={4}
        />
      ))}
    </div>
  )
}
